import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Users, 
    MessageSquare, 
    Calendar,
    Star,
    ChevronRight,
    User,
    MapPin,
    Briefcase,
    Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse date parts without timezone issues
const getDateParts = (dateString) => {
    if (!dateString) return { month: '', day: '' };
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        day: day
    };
};

const MemberDashboardPage = () => {
    const { member } = useAuth();
    const [spotlights, setSpotlights] = useState([]);
    const [events, setEvents] = useState([]);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [spotlightsRes, eventsRes, conversationsRes] = await Promise.all([
                axios.get(`${API}/member/spotlights`),
                axios.get(`${API}/member/events`),
                axios.get(`${API}/member/conversations`),
            ]);
            setSpotlights(spotlightsRes.data.slice(0, 3));
            setEvents(eventsRes.data.slice(0, 5));
            
            // Count unread messages
            const totalUnread = conversationsRes.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
            setUnreadMessages(totalUnread);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const profileCompletionPercentage = () => {
        const fields = [
            member?.name, member?.email, member?.phone, member?.profession,
            member?.location, member?.education, member?.birth_city,
            member?.hobbies?.length > 0, member?.interests?.length > 0,
            member?.profile_image, member?.employer
        ];
        const completed = fields.filter(Boolean).length;
        return Math.round((completed / fields.length) * 100);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-[#A0A0A0] animate-pulse">Loading...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="member-dashboard">
            {/* Welcome Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">
                        Welcome back, {member?.name?.split(' ')[0]}
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Your Recovery Room member dashboard
                    </p>
                </div>
                <div className="flex gap-3">
                    <Link to="/member/profile">
                        <Button 
                            variant="outline" 
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                            data-testid="edit-profile-btn"
                        >
                            <User className="w-4 h-4 mr-2" />
                            Edit Profile
                        </Button>
                    </Link>
                    <Link to="/member/settings">
                        <Button 
                            variant="outline" 
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                            data-testid="settings-btn"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Settings
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/member/directory">
                    <Card className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all cursor-pointer">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
                                <Users className="w-6 h-6 text-[#C9A227]" />
                            </div>
                            <div>
                                <p className="text-[#A0A0A0] text-sm">Member Directory</p>
                                <p className="text-white font-semibold">Browse Brothers</p>
                            </div>
                        </CardContent>
                    </Card>
                </Link>

                <Link to="/member/messages">
                    <Card className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all cursor-pointer">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#C9A227]/10 flex items-center justify-center relative">
                                <MessageSquare className="w-6 h-6 text-[#C9A227]" />
                                {unreadMessages > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                                        {unreadMessages > 9 ? '9+' : unreadMessages}
                                    </span>
                                )}
                            </div>
                            <div>
                                <p className="text-[#A0A0A0] text-sm">Messages</p>
                                <p className="text-white font-semibold">
                                    {unreadMessages > 0 ? `${unreadMessages} Unread` : 'Start Chatting'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </Link>

                <Link to="/member/events">
                    <Card className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all cursor-pointer">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-[#C9A227]" />
                            </div>
                            <div>
                                <p className="text-[#A0A0A0] text-sm">Community Events</p>
                                <p className="text-white font-semibold">{events.length} Upcoming</p>
                            </div>
                        </CardContent>
                    </Card>
                </Link>

                <Link to="/member/spotlights">
                    <Card className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all cursor-pointer">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
                                <Star className="w-6 h-6 text-[#C9A227]" />
                            </div>
                            <div>
                                <p className="text-[#A0A0A0] text-sm">Brother Spotlights</p>
                                <p className="text-white font-semibold">Read Stories</p>
                            </div>
                        </CardContent>
                    </Card>
                </Link>
            </div>

            {/* Profile Completion & Quick Profile */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Profile Completion */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <User className="w-5 h-5 text-[#C9A227]" />
                            Profile Completion
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-4">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-[#A0A0A0]">Profile strength</span>
                                <span className="text-[#C9A227] font-medium">{profileCompletionPercentage()}%</span>
                            </div>
                            <div className="h-2 bg-[#3D4435] rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-[#C9A227] to-[#E8D48A] transition-all duration-500"
                                    style={{ width: `${profileCompletionPercentage()}%` }}
                                />
                            </div>
                        </div>
                        {profileCompletionPercentage() < 100 && (
                            <Link to="/member/profile">
                                <Button 
                                    variant="outline" 
                                    className="w-full border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-[#3D4435]"
                                    data-testid="complete-profile-btn"
                                >
                                    Complete Your Profile
                                    <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        )}
                    </CardContent>
                </Card>

                {/* Your Info */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-white font-['Playfair_Display']">
                            Your Profile
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-start gap-4">
                            {member?.profile_image ? (
                                <img 
                                    src={member.profile_image.startsWith('/api/uploads') 
                                        ? `${process.env.REACT_APP_BACKEND_URL}${member.profile_image}`
                                        : member.profile_image.startsWith('/uploads')
                                            ? `${process.env.REACT_APP_BACKEND_URL}/api${member.profile_image}` 
                                            : member.profile_image
                                    }
                                    alt={member.name}
                                    className="w-16 h-16 rounded-xl object-cover"
                                    onError={(e) => {
                                        // Hide broken image and show initials fallback
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div 
                                className={`w-16 h-16 rounded-xl bg-[#C9A227] items-center justify-center text-[#3D4435] text-2xl font-bold flex-shrink-0 ${member?.profile_image ? 'hidden' : 'flex'}`}
                            >
                                {member?.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-white font-semibold">{member?.name}</h3>
                                <p className="text-[#A0A0A0] text-sm">@{member?.username}</p>
                                <div className="flex flex-wrap gap-3 mt-2 text-sm text-[#A0A0A0]">
                                    {member?.profession && (
                                        <span className="flex items-center gap-1">
                                            <Briefcase className="w-3 h-3" />
                                            {member.profession}
                                        </span>
                                    )}
                                    {member?.location && (
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {member.location}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Spotlights */}
            {spotlights.length > 0 && (
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <Star className="w-5 h-5 text-[#C9A227]" />
                            Brother Spotlights
                        </CardTitle>
                        <Link to="/member/spotlights">
                            <Button variant="ghost" className="text-[#C9A227] hover:text-[#E8D48A]">
                                View All
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {spotlights.map((spotlight) => (
                                <div 
                                    key={spotlight.id} 
                                    className="p-4 bg-[#3D4435] rounded-xl border border-[#5A6350]"
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        {spotlight.image_url ? (
                                            <img 
                                                src={spotlight.image_url} 
                                                alt={spotlight.member?.name}
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-[#C9A227] flex items-center justify-center text-[#3D4435] font-medium">
                                                {spotlight.member?.name?.charAt(0)}
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-white font-medium">{spotlight.member?.name}</p>
                                            <p className="text-[#666] text-xs">{spotlight.title}</p>
                                        </div>
                                    </div>
                                    <p className="text-[#A0A0A0] text-sm line-clamp-3">{spotlight.bio}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Upcoming Community Events */}
            {events.length > 0 && (
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-[#C9A227]" />
                            Community Events
                        </CardTitle>
                        <Link to="/member/events">
                            <Button variant="ghost" className="text-[#C9A227] hover:text-[#E8D48A]">
                                View All
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {events.map((event) => (
                                <div 
                                    key={event.id} 
                                    className="flex items-center gap-4 p-3 bg-[#3D4435] rounded-xl border border-[#5A6350]"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-[#C9A227]/10 flex flex-col items-center justify-center text-[#C9A227]">
                                        <span className="text-xs font-medium">
                                            {getDateParts(event.date).month}
                                        </span>
                                        <span className="text-lg font-bold leading-none">
                                            {getDateParts(event.date).day}
                                        </span>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-white font-medium">{event.title}</p>
                                        <p className="text-[#A0A0A0] text-sm">
                                            {event.location || 'Location TBD'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[#666] text-xs">
                                            Shared by {event.member?.name || 'Member'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default MemberDashboardPage;
